import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head } from "lodash";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { isNil } from "lodash";
import { i18n } from "../../translate/i18n";
import moment from "moment-timezone";
import useTimezone from "../../hooks/useTimezone";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChatiaDialog from "../ChatiaDialog";
import ChatiaDialogActions from "../ChatiaDialogActions";
import DatePickerCustom from "../DatePickerCustom";
import {
  Box,
  Grid,
  Tab,
  Tabs,
} from "@material-ui/core";
import {
  FormControl as MuiFormControl,
  InputLabel as MuiInputLabel,
  MenuItem as MuiMenuItem,
  Select as MuiSelect,
  TextField as MuiTextField,
} from "@mui/material";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import UserStatusIcon from "../UserModal/statusIcon";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import useQueues from "../../hooks/useQueues";

const CAMPAIGN_TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    CAMPAIGN_TIME_OPTIONS.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  messageTabs: {
    background: theme.palette.type === "dark" || theme.mode === "dark"
      ? "#121212"
      : "#ffffff",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" || theme.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.06)"
    }`,
    borderRadius: 0,
    padding: "0 16px",
    "& .MuiTabs-indicator": {
      height: 2,
      borderRadius: 2,
    },
    "& .MuiTab-root": {
      color: theme.palette.type === "dark" || theme.mode === "dark"
        ? "rgba(255,255,255,0.7)"
        : "rgba(0,0,0,0.65)",
      fontWeight: 500,
      fontSize: 13,
      minHeight: 44,
      textTransform: "none",
    },
    "& .MuiTab-root.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
}));

const CampaignSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("validation.tooShort"))
    .max(50, i18n.t("validation.tooLong"))
    .required(i18n.t("validation.required")),
});

const CampaignModal = ({
  open,
  onClose,
  campaignId,
  initialValues,
  onSave,
  resetPagination,
  defaultWhatsappId
}) => {

  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const { companyId } = user;

  const initialState = {
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    confirmationMessage1: "",
    confirmationMessage2: "",
    confirmationMessage3: "",
    confirmationMessage4: "",
    confirmationMessage5: "",
    status: "INATIVA", // INATIVA, PROGRAMADA, EM_ANDAMENTO, CANCELADA, FINALIZADA,
    confirmation: false,
    scheduledAt: "",
    //whatsappId: "",
    contactListId: "",
    tagListId: "",
    companyId,
    statusTicket: "closed",
    openTicket: "disabled"
  };

  const [campaign, setCampaign] = useState(initialState);
  const [whatsapps, setWhatsapps] = useState([]);
  const [whatsappId, setWhatsappId] = useState(false);

  // Timezone
  const { getCompanyTimezone } = useTimezone();
  const [companyTimezone, setCompanyTimezone] = useState("America/Sao_Paulo");
  const [currentTimeInTz, setCurrentTimeInTz] = useState("");

  useEffect(() => {
    let interval;
    if (open) {
      const loadTz = async () => {
        try {
          const tzData = await getCompanyTimezone(companyId);
          const tz = tzData.timezone;
          setCompanyTimezone(tz);
          setCurrentTimeInTz(moment().tz(tz).format('DD/MM/YYYY HH:mm:ss'));
          interval = setInterval(() => {
            setCurrentTimeInTz(moment().tz(tz).format('DD/MM/YYYY HH:mm:ss'));
          }, 1000);
        } catch {}
      };
      loadTz();
      return () => { if (interval) clearInterval(interval); };
    }
  }, [open, companyId, getCompanyTimezone]);

useEffect(() => {
    if (!campaignId && defaultWhatsappId) {
      setWhatsappId(defaultWhatsappId);
    }
  }, [defaultWhatsappId, campaignId]);

  const [contactLists, setContactLists] = useState([]);
  const [tagLists, setTagLists] = useState([]);
  const [messageTab, setMessageTab] = useState(0);
  const [attachment, setAttachment] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const attachmentFile = useRef(null);

  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const { findAll: findAllQueues } = useQueues();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);

      };
      loadQueues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  

  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/");
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    if (isMounted.current) {
      if (initialValues) {
        setCampaign((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      api
        .get(`/contact-lists/list`, { params: { companyId } })
        .then(({ data }) => setContactLists(data));

      api
        .get(`/whatsapp`, { params: { companyId, session: 0 } })
        .then(({ data }) => {
          // Mapear os dados recebidos da API para adicionar a propriedade 'selected'
          const mappedWhatsapps = data.map((whatsapp) => ({
            ...whatsapp,
            selected: false,
          }));

          setWhatsapps(mappedWhatsapps);
        });

      api.get(`/tags/list`, { params: { companyId } })
        .then((response) => {
          if (!response || !response.data) {
            console.warn("[CampaignModal] /tags/list retornou resposta vazia");
            return;
          }
          const fetchedTags = response.data;
          console.log("[CampaignModal] Tags recebidas:", fetchedTags.length, "companyId:", companyId);
          const formattedTagLists = fetchedTags
            .filter(tag => Number(tag.contactsCount || 0) > 0)
            .map((tag) => ({
              id: tag.id,
              name: `${tag.name} (${Number(tag.contactsCount || 0)})`,
            }));

          console.log("[CampaignModal] Tags com contatos:", formattedTagLists.length);
          setTagLists(formattedTagLists);
        })
        .catch((error) => {
          console.error("[CampaignModal] Erro ao buscar tags:", error);
        });

      if (!campaignId) return;

      api.get(`/campaigns/${campaignId}`).then(({ data }) => {

        if (data?.user)
          setSelectedUser(data.user);

        if (data?.queue)
          setSelectedQueue(data.queue.id)

        if (data?.whatsappId) {
          // const selectedWhatsapps = data.whatsappId.split(",");
          setWhatsappId(data.whatsappId);
        }
        setCampaign((prev) => {
          let prevCampaignData = Object.assign({}, prev);

          Object.entries(data).forEach(([key, value]) => {
            if (key === "scheduledAt" && value !== "" && value !== null) {
              // Converter de UTC para timezone da empresa
              prevCampaignData[key] = moment.utc(value).tz(companyTimezone).format("YYYY-MM-DDTHH:mm");
            } else {
              prevCampaignData[key] = value === null ? "" : value;
            }
          });

          return prevCampaignData;
        });
      });
    }
  }, [campaignId, open, initialValues, companyId, companyTimezone]);

  useEffect(() => {
    const now = moment();
    const scheduledAt = moment(campaign.scheduledAt);
    const moreThenAnHour =
      !Number.isNaN(scheduledAt.diff(now)) && scheduledAt.diff(now, "hour") > 1;
    const isEditable =
      campaign.status === "INATIVA" ||
      (campaign.status === "PROGRAMADA" && moreThenAnHour);

    setCampaignEditable(isEditable);
  }, [campaign.status, campaign.scheduledAt]);

  const handleClose = () => {
    onClose();
    setCampaign(initialState);
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveCampaign = async (values) => {
    try {
      const dataValues = {
        ...values,  // Merge the existing values object
        whatsappId: whatsappId,
        userId: selectedUser?.id || null,
        queueId: selectedQueue || null
      };

      //console.log(values);
      //console.log(selectedWhatsapps);

      Object.entries(values).forEach(([key, value]) => {
        if (key === "scheduledAt" && value !== "" && value !== null) {
          // Enviar como string pura - backend converte do timezone da empresa para UTC
          dataValues[key] = value;
        } else {
          dataValues[key] = value === "" ? null : value;
        }
      });

      if (campaignId) {
        await api.put(`/campaigns/${campaignId}`, dataValues);

        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${campaignId}/media-upload`, formData);
        }
        handleClose();
      } else {
        const { data } = await api.post("/campaigns", dataValues);

        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${data.id}/media-upload`, formData);
        }
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("campaigns.toasts.success"));
    } catch (err) {
      console.log(err);
      toastError(err);
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (campaign.mediaPath) {
      await api.delete(`/campaigns/${campaign.id}/media-upload`);
      setCampaign((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("campaigns.toasts.deleted"));
    }
  };

  const renderMessageField = (identifier) => {
    return (
      <Field
        as={TextField}
        id={identifier}
        name={identifier}
        fullWidth
        minRows={5}
        label={i18n.t(`campaigns.dialog.form.${identifier}`)}
        placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
        multiline={true}
        variant="outlined"
        helperText={i18n.t("campaigns.modal.helpText")}
        disabled={!campaignEditable && campaign.status !== "CANCELADA"}
      />
    );
  };

  const renderConfirmationMessageField = (identifier) => {
    return (
      <Field
        as={TextField}
        id={identifier}
        name={identifier}
        fullWidth
        minRows={5}
        label={i18n.t(`campaigns.dialog.form.${identifier}`)}
        placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
        multiline={true}
        variant="outlined"
        disabled={!campaignEditable && campaign.status !== "CANCELADA"}
      />
    );
  };

  const cancelCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filterOptions = createFilterOptions({
    trim: true,
  });

  const dialogTitle = campaignEditable
    ? campaignId
      ? i18n.t("campaigns.dialog.update")
      : i18n.t("campaigns.dialog.new")
    : i18n.t("campaigns.dialog.readonly");
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  const dialogSurface = isDark ? "#121212" : "#ffffff";

  const reportFieldSx = {
    mt: "8px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      minHeight: 40,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "12px !important",
    },
    "& .MuiInputBase-root": {
      borderRadius: "12px !important",
    },
    "& .MuiInputLabel-root": {
      backgroundColor: dialogSurface,
      px: "4px",
    },
  };

  const reportSelectMenuProps = {
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: "14px",
        boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
        border: "none",
        backgroundImage: "none",
        overflow: "hidden",
        "& .MuiMenu-list": {
          padding: "6px",
        },
        "& .MuiMenuItem-root": {
          borderRadius: "8px",
          padding: "9px 12px",
          marginBottom: "4px",
          fontSize: 14,
          fontWeight: 500,
          "&.Mui-selected": {
            background: "rgba(6, 207, 156, 0.10)",
          },
          "&.Mui-selected:hover": {
            background: "rgba(6, 207, 156, 0.16)",
          },
        },
      },
    },
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ChatiaDialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        title={dialogTitle}
        hideFooter
      >
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={campaign}
          enableReinitialize={true}
          validationSchema={CampaignSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCampaign(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
                <Grid spacing={2} container>
                  <Grid xs={12} md={4} item>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.name")}
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>
                  <Grid xs={12} md={4} item>
                    <MuiFormControl
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={reportFieldSx}
                    >
                      <MuiInputLabel id="confirmation-selection-label">
                        {i18n.t("campaigns.dialog.form.confirmation")}
                      </MuiInputLabel>
                      <Field
                        as={MuiSelect}
                        label={i18n.t("campaigns.dialog.form.confirmation")}
                        placeholder={i18n.t(
                          "campaigns.dialog.form.confirmation"
                        )}
                        labelId="confirmation-selection-label"
                        id="confirmation"
                        name="confirmation"
                        error={
                          touched.confirmation && Boolean(errors.confirmation)
                        }
                        disabled={!campaignEditable}
                        MenuProps={reportSelectMenuProps}
                      >
                        <MuiMenuItem value={false}>{i18n.t("campaigns.common.disabled")}</MuiMenuItem>
                        <MuiMenuItem value={true}>{i18n.t("campaigns.common.enabled")}</MuiMenuItem>
                      </Field>
                    </MuiFormControl>
                  </Grid>
                  <Grid xs={12} md={4} item>
                    <MuiFormControl
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={reportFieldSx}
                    >
                      <MuiInputLabel id="contactList-selection-label">
                        {i18n.t("campaigns.dialog.form.contactList")}
                      </MuiInputLabel>
                      <Field
                        as={MuiSelect}
                        label={i18n.t("campaigns.dialog.form.contactList")}
                        placeholder={i18n.t(
                          "campaigns.dialog.form.contactList"
                        )}
                        labelId="contactList-selection-label"
                        id="contactListId"
                        name="contactListId"
                        error={
                          touched.contactListId && Boolean(errors.contactListId)
                        }
                        disabled={!campaignEditable}
                        MenuProps={reportSelectMenuProps}
                      >
                        <MuiMenuItem value="">{i18n.t("campaigns.common.none")}</MuiMenuItem>
                        {contactLists &&
                          contactLists.map((contactList) => (
                            <MuiMenuItem
                              key={contactList.id}
                              value={contactList.id}
                            >
                              {contactList.name}
                            </MuiMenuItem>
                          ))}
                      </Field>
                    </MuiFormControl>
                  </Grid>
                  <Grid xs={12} md={4} item>
                    <MuiFormControl
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={reportFieldSx}
                    >
                      <MuiInputLabel id="tagList-selection-label">
                        {i18n.t("campaigns.dialog.form.tagList")}
                      </MuiInputLabel>
                      <Field
                        as={MuiSelect}
                        label={i18n.t("campaigns.dialog.form.tagList")}
                        placeholder={i18n.t("campaigns.dialog.form.tagList")}
                        labelId="tagList-selection-label"
                        id="tagListId"
                        name="tagListId"
                        error={touched.tagListId && Boolean(errors.tagListId)}
                        disabled={!campaignEditable}
                        MenuProps={reportSelectMenuProps}
                      >
                        <MuiMenuItem value="">{i18n.t("campaigns.common.none")}</MuiMenuItem>
                        {Array.isArray(tagLists) &&
                          tagLists.map((tagList) => (
                            <MuiMenuItem key={tagList.id} value={tagList.id}>
                              {tagList.name}
                            </MuiMenuItem>
                          ))}
                      </Field>
                    </MuiFormControl>
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <MuiFormControl
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={reportFieldSx}
                    >
                      <MuiInputLabel id="whatsapp-selection-label">
                        {i18n.t("campaigns.dialog.form.whatsapp")}
                      </MuiInputLabel>
                      <MuiSelect
                        // multiple
                        label={i18n.t("campaigns.dialog.form.whatsapp")}
                        placeholder={i18n.t("campaigns.dialog.form.whatsapp")}
                        labelId="whatsapp-selection-label"
                        id="whatsappIds"
                        name="whatsappIds"
                        required
                        error={touched.whatsappId && Boolean(errors.whatsappId)}
                        disabled={!campaignEditable}
                        value={whatsappId}
                        MenuProps={reportSelectMenuProps}
                        onChange={(event) => {
                          console.log(event.target.value)
                          setWhatsappId(event.target.value)
                        }}
                        // renderValue={(selected) => (
                        //   <div>
                        //     {selected.map((value) => (
                        //       <Chip key={value} label={whatsapps.find((whatsapp) => whatsapp.id === value).name} />
                        //     ))}
                        //   </div>
                        // )}
                      >
                        {whatsapps &&
                          whatsapps.map((whatsapp) => (
                            <MuiMenuItem key={whatsapp.id} value={whatsapp.id}>
                              {whatsapp.name}
                            </MuiMenuItem>
                          ))}
                      </MuiSelect>
                    </MuiFormControl>
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <Grid container spacing={1}>
                      <Grid xs={12} md={7} item>
                        <DatePickerCustom
                          label={i18n.t("campaigns.dialog.form.scheduledAt")}
                          value={values.scheduledAt ? values.scheduledAt.split("T")[0] : ""}
                          onChange={(dateStr) => {
                            const time = values.scheduledAt && values.scheduledAt.includes("T")
                              ? values.scheduledAt.split("T")[1]
                              : "09:00";
                            setFieldValue("scheduledAt", dateStr ? `${dateStr}T${time}` : "");
                          }}
                          disabled={!campaignEditable}
                        />
                      </Grid>
                      <Grid xs={12} md={5} item>
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={CAMPAIGN_TIME_OPTIONS}
                          inputValue={values.scheduledAt && values.scheduledAt.includes("T") ? values.scheduledAt.split("T")[1] : "09:00"}
                          onInputChange={(e, v, reason) => {
                            if (reason === "reset") return;
                            const digits = v.replace(/\D/g, "");
                            let next = v;
                            if (digits.length >= 4 && !v.includes(":")) {
                              const hh = Math.min(parseInt(digits.slice(0, 2), 10), 23);
                              const mm = Math.min(parseInt(digits.slice(2, 4), 10), 59);
                              next = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
                            }
                            const date = values.scheduledAt && values.scheduledAt.includes("T")
                              ? values.scheduledAt.split("T")[0]
                              : moment().tz(companyTimezone).format("YYYY-MM-DD");
                            setFieldValue("scheduledAt", `${date}T${next}`);
                          }}
                          onChange={(e, v) => {
                            if (!v) return;
                            const date = values.scheduledAt && values.scheduledAt.includes("T")
                              ? values.scheduledAt.split("T")[0]
                              : moment().tz(companyTimezone).format("YYYY-MM-DD");
                            setFieldValue("scheduledAt", `${date}T${v}`);
                          }}
                          disableClearable
                          disabled={!campaignEditable}
                          getOptionLabel={(o) => o || ""}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              margin="dense"
                              label={i18n.t("scheduleModal.form.time") || "Hora"}
                              placeholder="09:00"
                              InputLabelProps={{ shrink: true }}
                              error={touched.scheduledAt && Boolean(errors.scheduledAt)}
                              helperText={
                                touched.scheduledAt && errors.scheduledAt
                                  ? errors.scheduledAt
                                  : `Timezone: ${companyTimezone}${currentTimeInTz ? ` | ${currentTimeInTz}` : ""}`
                              }
                            />
                          )}
                          ListboxProps={{ style: { maxHeight: 200 } }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid xs={12} md={4} item>
                    <MuiFormControl
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={reportFieldSx}
                    >
                      <MuiInputLabel id="openTicket-selection-label">
                        {i18n.t("campaigns.dialog.form.openTicket")}
                      </MuiInputLabel>
                      <Field
                        as={MuiSelect}
                        label={i18n.t("campaigns.dialog.form.openTicket")}
                        placeholder={i18n.t(
                          "campaigns.dialog.form.openTicket"
                        )}
                        labelId="openTicket-selection-label"
                        id="openTicket"
                        name="openTicket"
                        error={
                          touched.openTicket && Boolean(errors.openTicket)
                        }
                        disabled={!campaignEditable}
                        MenuProps={reportSelectMenuProps}
                      >
                        <MuiMenuItem value={"enabled"}>{i18n.t("campaigns.dialog.form.enabledOpenTicket")}</MuiMenuItem>
                        <MuiMenuItem value={"disabled"}>{i18n.t("campaigns.dialog.form.disabledOpenTicket")}</MuiMenuItem>
                      </Field>
                    </MuiFormControl>
                  </Grid>
                  {/* SELECIONAR USUARIO */}
                  <Grid xs={12} md={4} item>
                    <Autocomplete
                      style={{ marginTop: '8px' }}
                      variant="outlined"
                      margin="dense"
                      className={classes.formControl}
                      getOptionLabel={(option) => `${option.name}`}
                      value={selectedUser}
                      size="small"
                      onChange={(e, newValue) => {
                        setSelectedUser(newValue);
                        if (newValue != null && Array.isArray(newValue.queues)) {
                          if (newValue.queues.length === 1) {
                            setSelectedQueue(newValue.queues[0].id);
                          }
                          setQueues(newValue.queues);

                        } else {
                          setQueues(allQueues);
                          setSelectedQueue("");
                        }
                      }}
                      options={options}
                      filterOptions={filterOptions}
                      freeSolo
                      fullWidth
                      autoHighlight
                      disabled={!campaignEditable || values.openTicket === 'disabled'}
                      noOptionsText={i18n.t("transferTicketModal.noOptions")}
                      loading={loading}
                      renderOption={option => (<span> <UserStatusIcon user={option} /> {option.name}</span>)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={i18n.t("transferTicketModal.fieldLabel")}
                          variant="outlined"
                          onChange={(e) => setSearchParam(e.target.value)}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid xs={12} md={4} item>
                    <MuiFormControl
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={reportFieldSx}
                    >
                      <MuiInputLabel>
                        {i18n.t("transferTicketModal.fieldQueueLabel")}
                      </MuiInputLabel>
                      <MuiSelect
                        value={selectedQueue}
                        onChange={(e) => setSelectedQueue(e.target.value)}
                        label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
                        required={!isNil(selectedUser)}
                        disabled={!campaignEditable || values.openTicket === 'disabled'}
                        MenuProps={reportSelectMenuProps}
                      >
                        {queues.map((queue) => (
                          <MuiMenuItem key={queue.id} value={queue.id}>
                            {queue.name}
                          </MuiMenuItem>
                        ))}
                      </MuiSelect>
                    </MuiFormControl>
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <MuiFormControl
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={reportFieldSx}
                    >
                      <MuiInputLabel id="statusTicket-selection-label">
                        {i18n.t("campaigns.dialog.form.statusTicket")}
                      </MuiInputLabel>
                      <Field
                        as={MuiSelect}
                        label={i18n.t("campaigns.dialog.form.statusTicket")}
                        placeholder={i18n.t(
                          "campaigns.dialog.form.statusTicket"
                        )}
                        labelId="statusTicket-selection-label"
                        id="statusTicket"
                        name="statusTicket"
                        error={
                          touched.statusTicket && Boolean(errors.statusTicket)
                        }
                        disabled={!campaignEditable || values.openTicket === 'disabled'}
                        MenuProps={reportSelectMenuProps}
                      >
                        <MuiMenuItem value={"closed"}>{i18n.t("campaigns.dialog.form.closedTicketStatus")}</MuiMenuItem>
                        <MuiMenuItem value={"pending"}>{i18n.t("campaigns.dialog.form.pendingTicketStatus")}</MuiMenuItem>
                        <MuiMenuItem value={"open"}>{i18n.t("campaigns.dialog.form.openTicketStatus")}</MuiMenuItem>
                      </Field>
                    </MuiFormControl>
                  </Grid>

                  <Grid xs={12} item>
                    <Tabs
                      value={messageTab}
                      indicatorColor="primary"
                      textColor="primary"
                      onChange={(e, v) => setMessageTab(v)}
                      variant="fullWidth"
                      centered
                      className={classes.messageTabs}
                    >
                      <Tab label={i18n.t("campaigns.modal.tabLabels.msg1")} index={0} />
                      <Tab label={i18n.t("campaigns.modal.tabLabels.msg2")} index={1} />
                      <Tab label={i18n.t("campaigns.modal.tabLabels.msg3")} index={2} />
                      <Tab label={i18n.t("campaigns.modal.tabLabels.msg4")} index={3} />
                      <Tab label={i18n.t("campaigns.modal.tabLabels.msg5")} index={4} />
                    </Tabs>
                    <Box style={{ paddingTop: 20, border: "none" }}>
                      {messageTab === 0 && (
                        <>
                          {values.confirmation ? (
                            <Grid spacing={2} container>
                              <Grid xs={12} md={8} item>
                                <>{renderMessageField("message1")}</>
                              </Grid>
                              <Grid xs={12} md={4} item>
                                <>
                                  {renderConfirmationMessageField(
                                    "confirmationMessage1"
                                  )}
                                </>
                              </Grid>
                            </Grid>
                          ) : (
                            <>{renderMessageField("message1")}</>
                          )}
                        </>
                      )}
                      {messageTab === 1 && (
                        <>
                          {values.confirmation ? (
                            <Grid spacing={2} container>
                              <Grid xs={12} md={8} item>
                                <>{renderMessageField("message2")}</>
                              </Grid>
                              <Grid xs={12} md={4} item>
                                <>
                                  {renderConfirmationMessageField(
                                    "confirmationMessage2"
                                  )}
                                </>
                              </Grid>
                            </Grid>
                          ) : (
                            <>{renderMessageField("message2")}</>
                          )}
                        </>
                      )}
                      {messageTab === 2 && (
                        <>
                          {values.confirmation ? (
                            <Grid spacing={2} container>
                              <Grid xs={12} md={8} item>
                                <>{renderMessageField("message3")}</>
                              </Grid>
                              <Grid xs={12} md={4} item>
                                <>
                                  {renderConfirmationMessageField(
                                    "confirmationMessage3"
                                  )}
                                </>
                              </Grid>
                            </Grid>
                          ) : (
                            <>{renderMessageField("message3")}</>
                          )}
                        </>
                      )}
                      {messageTab === 3 && (
                        <>
                          {values.confirmation ? (
                            <Grid spacing={2} container>
                              <Grid xs={12} md={8} item>
                                <>{renderMessageField("message4")}</>
                              </Grid>
                              <Grid xs={12} md={4} item>
                                <>
                                  {renderConfirmationMessageField(
                                    "confirmationMessage4"
                                  )}
                                </>
                              </Grid>
                            </Grid>
                          ) : (
                            <>{renderMessageField("message4")}</>
                          )}
                        </>
                      )}
                      {messageTab === 4 && (
                        <>
                          {values.confirmation ? (
                            <Grid spacing={2} container>
                              <Grid xs={12} md={8} item>
                                <>{renderMessageField("message5")}</>
                              </Grid>
                              <Grid xs={12} md={4} item>
                                <>
                                  {renderConfirmationMessageField(
                                    "confirmationMessage5"
                                  )}
                                </>
                              </Grid>
                            </Grid>
                          ) : (
                            <>{renderMessageField("message5")}</>
                          )}
                        </>
                      )}
                    </Box>
                  </Grid>
                  {(campaign.mediaPath || attachment) && (
                    <Grid xs={12} item>
                      <Button startIcon={<AttachFileIcon />}>
                        {attachment != null
                          ? attachment.name
                          : campaign.mediaName}
                      </Button>
                      {campaignEditable && (
                        <IconButton
                          onClick={() => setConfirmationOpen(true)}
                          color="primary"
                        >
                          <DeleteOutlineIcon color="secondary" />
                        </IconButton>
                      )}
                    </Grid>
                  )}
                </Grid>
              {(campaignEditable || campaign.status === "CANCELADA") ? (
                <ChatiaDialogActions
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  saveText={campaignId
                    ? `${i18n.t("campaigns.dialog.buttons.edit")}`
                    : `${i18n.t("campaigns.dialog.buttons.add")}`}
                >
                  {campaign.status === "CANCELADA" && (
                    <Button
                      color="primary"
                      onClick={() => restartCampaign()}
                      variant="outlined"
                    >
                      {i18n.t("campaigns.dialog.buttons.restart")}
                    </Button>
                  )}
                  {campaign.status === "EM_ANDAMENTO" && (
                    <Button
                      color="primary"
                      onClick={() => cancelCampaign()}
                      variant="outlined"
                    >
                      {i18n.t("campaigns.dialog.buttons.cancel")}
                    </Button>
                  )}
                  {!attachment && !campaign.mediaPath && campaignEditable && (
                    <Button
                      color="primary"
                      onClick={() => attachmentFile.current.click()}
                      disabled={isSubmitting}
                      variant="outlined"
                    >
                      {i18n.t("campaigns.dialog.buttons.attach")}
                    </Button>
                  )}
                </ChatiaDialogActions>
              ) : (
                <ChatiaDialogActions
                  type="button"
                  onClick={handleClose}
                  saveText={i18n.t("campaigns.dialog.buttons.close")}
                >
                  {campaign.status === "EM_ANDAMENTO" && (
                    <Button
                      color="primary"
                      onClick={() => cancelCampaign()}
                      variant="outlined"
                    >
                      {i18n.t("campaigns.dialog.buttons.cancel")}
                    </Button>
                  )}
                </ChatiaDialogActions>
              )}
            </Form>
          )}
        </Formik>
      </ChatiaDialog>
    </div>
  );
};

export default CampaignModal;
